import { useCallback } from "react";
import type { ChartConfig, TickProps } from "@ptsd/uikit";
import {
  Card,
  ChartContainer,
  ChartTooltip,
  getChartLines,
  Legend,
  YAxisTick,
} from "@ptsd/uikit";
import {
  CartesianGrid,
  Line,
  LineChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Circle, Text, View, XStack, YStack } from "tamagui";

import type { RouterOutputs } from "~/utils/api";
import { dateToDDMM, dateToReadable } from "~/utils/date-to-readable";

type SliceDynamics = RouterOutputs["slices"]["getResults"];

export const SliceDynamics: React.FC<{ data: SliceDynamics }> = ({ data }) => {
  const toReadableDate = useCallback(
    (date: string | undefined) => dateToReadable(date, true),
    [],
  );

  const toReadableDDMM = useCallback(
    (date: string | undefined) => dateToDDMM(date),
    [],
  );

  const config = {
    percentage: {
      label: data.main.title,
      color: "#EC1E9D",
    },
  } satisfies ChartConfig;

  if (!data) return null;

  return (
    <Card
      data-id="slice-dynamics-chart"
      paddingVertical={"$4.1"}
      shadow="md"
      width={"100%"}
      gap={"$4.1"}
      display={data.history.length > 1 ? "flex" : "none"}
    >
      <XStack justifyContent="space-between" alignItems="center">
        <Card.Title>{data.main.title}</Card.Title>
      </XStack>

      <View position="relative" gap={"$3"}>
        <ChartContainer
          data-id="slice-dynamics-chart-container"
          height={362}
          config={config}
        >
          <LineChart
            data-id="slice-dynamics-chart-linechart"
            accessibilityLayer
            data={data.history}
            margin={{
              left: 0,
              right: 40,
              top: 12,
              bottom: 20,
            }}
          >
            <CartesianGrid
              strokeDasharray={"3 3"}
              stroke="#CBCBCC"
              vertical={false}
            />
            <XAxis
              data-id="slice-dynamics-chart-x-axis"
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={24}
              tick={{
                fill: "#636668",
                fontFamily: "Fixel Text",
                fontSize: 14,
                fontWeight: 400,
              }}
              tickFormatter={(v: string | number) => {
                return toReadableDDMM(v.toString()) ?? "";
              }}
            />
            <YAxis
              data-id="slice-dynamics-chart-y-axis"
              tickLine={false}
              axisLine={false}
              tickMargin={16}
              tick={(props: TickProps) => <YAxisTick {...props} />}
            />
            <Tooltip
              data-id="slice-dynamics-chart-tooltip"
              cursor={{
                stroke: "#CBCBCC",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
              content={
                <ChartTooltip.Content data-id="slice-dynamics-chart-tooltip-content">
                  {(payload, _, date) => (
                    <ChartTooltip.Container data-id="slice-dynamics-chart-tooltip-container">
                      <ChartTooltip.Title data-id="slice-dynamics-chart-tooltip-title">
                        {toReadableDate(date)}
                      </ChartTooltip.Title>
                      <View gap={"$0.5"}>
                        {payload?.map(({ color, value, config }, i) => (
                          <XStack
                            data-id={`slice-dynamics-chart-tooltip-item`}
                            data-value={value}
                            gap={"$0.5"}
                            alignItems="center"
                            key={i}
                          >
                            <Circle
                              backgroundColor={"white"}
                              borderWidth={"$0.3"}
                              borderColor={color}
                              size={"$2"}
                            />

                            <Text
                              fontFamily={"$body"}
                              fontSize={"$1"}
                              color={"black"}
                            >
                              {config?.label} {value}
                            </Text>
                          </XStack>
                        ))}
                      </View>
                    </ChartTooltip.Container>
                  )}
                </ChartTooltip.Content>
              }
            />
            {getChartLines(config).map(({ dataKey, color }, i) => (
              <Line
                data-id={"slice-dynamics-chart-line"}
                data-key={dataKey}
                key={i}
                dataKey={dataKey}
                type="linear"
                stroke={color}
                strokeWidth={2.5}
                dot={{
                  r: 8,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: "white",
                }}
                activeDot={{
                  r: 3,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: color,
                }}
              />
            ))}
          </LineChart>
        </ChartContainer>

        <Legend config={config} />
      </View>
    </Card>
  );
};

export const SlicesDynmaicsChartMobile: React.FC<{ data: SliceDynamics }> = ({
  data,
}) => {
  const toReadableDate = useCallback(
    (date: string | undefined) => dateToReadable(date, true),
    [],
  );

  const toReadableDDMM = useCallback(
    (date: string | undefined) => dateToDDMM(date),
    [],
  );

  const config = {
    percentage: {
      label: data.main.title,
      color: "#EC1E9D",
    },
  } satisfies ChartConfig;

  if (!data) return null;

  return (
    <View
      width={"100%"}
      backgroundColor={"$warm-snow-500"}
      paddingVertical={"$4"}
      paddingHorizontal={"$3"}
      gap={"$3"}
      display={data.history.length > 1 ? "flex" : "none"}
    >
      <YStack justifyContent="center" gap={"$2"} alignItems="center">
        <Text
          fontFamily={"$subtitle"}
          fontWeight={"$6"}
          fontSize={"$5"}
          lineHeight={"$5"}
          textAlign={"center"}
          color={"$gray-primary-900"}
        >
          {data.main.title}
        </Text>
      </YStack>

      <View gap={"$3"} position="relative">
        {/* <Text */}
        {/*   position="absolute" */}
        {/*   rotate="-90deg" */}
        {/*   style={{ */}
        {/*     left: -20, */}
        {/*     top: "25%", */}
        {/*   }} */}
        {/*   fontFamily={"$subtitle"} */}
        {/*   fontWeight={"$4"} */}
        {/*   fontSize={"$3"} */}
        {/*   color={"$gray-650"} */}
        {/* > */}
        {/*   Учасники */}
        {/* </Text> */}
        <ChartContainer height={362} config={config}>
          <LineChart
            accessibilityLayer
            data={data.history}
            margin={{
              left: 0,
              right: 40,
              top: 12,
              bottom: 24,
            }}
          >
            <CartesianGrid
              strokeDasharray={"3 3"}
              stroke="#CBCBCC"
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={12}
              tick={{
                fill: "#636668",
                fontFamily: "Fixel Text",
                fontSize: 14,
                fontWeight: 400,
              }}
              tickFormatter={(v: string | number) => {
                return toReadableDDMM(v.toString()) ?? "";
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickMargin={16}
              tick={(props: TickProps) => <YAxisTick {...props} />}
            />
            <Tooltip
              cursor={{
                stroke: "#CBCBCC",
                strokeWidth: 1,
                strokeDasharray: "3 3",
              }}
              content={
                <ChartTooltip.Content>
                  {(payload, _, date) => (
                    <ChartTooltip.Container>
                      <ChartTooltip.Title>
                        {toReadableDate(date)}
                      </ChartTooltip.Title>
                      <View gap={"$0.5"}>
                        {payload?.map(({ color, value, config }, i) => (
                          <XStack gap={"$0.5"} alignItems="center" key={i}>
                            <Circle
                              backgroundColor={"white"}
                              borderWidth={"$0.3"}
                              borderColor={color}
                              size={"$2"}
                            />

                            <Text
                              fontFamily={"$body"}
                              fontSize={"$1"}
                              color={"black"}
                            >
                              {config?.label} {value}
                            </Text>
                          </XStack>
                        ))}
                      </View>
                    </ChartTooltip.Container>
                  )}
                </ChartTooltip.Content>
              }
            />
            {getChartLines(config).map(({ dataKey, color }, i) => (
              <Line
                key={i}
                dataKey={dataKey}
                type="linear"
                stroke={color}
                strokeWidth={2.5}
                dot={{
                  r: 8,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: "white",
                }}
                activeDot={{
                  r: 3,
                  strokeWidth: 2.5,
                  stroke: color,
                  fill: color,
                }}
              />
            ))}
          </LineChart>
        </ChartContainer>

        <Legend backgroundColor={"white"} config={config} />
      </View>
    </View>
  );
};
